import React, { useEffect, useMemo, useState } from "react";
import {
  BookOpen,
  PlayCircle,
  MessageCircle,
  Activity,
  LifeBuoy,
  Send,
  Paperclip,
  CheckCircle2,
  AlertCircle,
  X,
} from "lucide-react";

const SUPPORT_API_BASE_URL =
  process.env.REACT_APP_BASE_URL || "https://studioapi.r-link.com/api";
const SUPPORT_TICKETS_URL = `${SUPPORT_API_BASE_URL}/support/tickets`;

const CATEGORIES = [
  { value: "billing", label: "Billing" },
  { value: "technical", label: "Technical" },
  { value: "account", label: "Account" },
  { value: "room-webinar", label: "Room/Webinar" },
  { value: "other", label: "Other" },
];

const SUBJECT_MAX = 150;
const DESCRIPTION_MIN = 20;

const RESOURCE_CARDS = [
  {
    key: "docs",
    title: "Documentation",
    desc: "Comprehensive guides and API reference",
    icon: BookOpen,
    color: "#3b82f6",
    href: null,
  },
  {
    key: "videos",
    title: "Video Tutorials",
    desc: "Step-by-step video guides",
    icon: PlayCircle,
    color: "#a64dff",
    href: "https://r-link.com/tutorials",
  },
  {
    key: "forum",
    title: "Community Forum",
    desc: "Connect with other R-Link users",
    icon: MessageCircle,
    color: "#00c853",
    href: "https://impact.connect.win/landing?from=https%3A%2F%2Fimpact.connect.win%2Flogin",
  },
  {
    key: "status",
    title: "System Status",
    desc: "Check service availability",
    icon: Activity,
    color: "#f59e0b",
    href: null,
  },
];

const STATUS_STYLES = {
  open:    { bg: "#E6F1FB", color: "#185FA5", label: "Open" },
  pending: { bg: "#FAEEDA", color: "#854F0B", label: "Pending" },
  solved:  { bg: "#EAF3DE", color: "#3B6D11", label: "Solved" },
  closed:  { bg: "#F1EFE8", color: "#5F5E5A", label: "Closed" },
};

const formatRelativeTime = (value) => {
  if (!value) return "";
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return "";
  const diffMs = Date.now() - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
  const years = Math.floor(months / 12);
  return `${years} year${years === 1 ? "" : "s"} ago`;
};

const formatTicketNumber = (ticket) => {
  const raw = ticket.zendesk_ticket_id || ticket.id || "";
  const digits = String(raw).replace(/\D/g, "");
  if (!digits) return `TKT-${String(raw).slice(-4).toUpperCase()}`;
  return `TKT-${digits.padStart(3, "0")}`;
};

const HelpSupport = () => {
  const isDark = true;

  const [isMobile, setIsMobile] = useState(false);

  const [category, setCategory] = useState("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [attachment, setAttachment] = useState(null);

  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsError, setTicketsError] = useState("");

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [detailTicket, setDetailTicket] = useState(null);
  const [detailComments, setDetailComments] = useState([]);

  const [replyBody, setReplyBody] = useState("");
  const [replyAttachment, setReplyAttachment] = useState(null);
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [replyError, setReplyError] = useState("");
  const [replySuccess, setReplySuccess] = useState("");

  useEffect(() => {
    if (!replySuccess) return;
    const id = window.setTimeout(() => setReplySuccess(""), 4000);
    return () => window.clearTimeout(id);
  }, [replySuccess]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const token = useMemo(() => {
    try { return localStorage.getItem("token") || ""; } catch { return ""; }
  }, []);

  const loadTickets = async () => {
    if (!token) return;
    setTicketsLoading(true);
    setTicketsError("");
    try {
      const response = await fetch(SUPPORT_TICKETS_URL, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "rlink-token": token,
        },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || `Failed to load tickets (${response.status}).`);
      }
      const list = Array.isArray(data?.tickets) ? data.tickets : [];
      setTickets(list);
    } catch (err) {
      setTicketsError(err?.message || "Failed to load tickets.");
      setTickets([]);
    } finally {
      setTicketsLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openTicketDetail = async (ticketId) => {
    if (!token) return;
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError("");
    setDetailTicket(null);
    setDetailComments([]);
    setReplyBody("");
    setReplyAttachment(null);
    setReplyError("");
    setReplySuccess("");
    try {
      const response = await fetch(`${SUPPORT_TICKETS_URL}/${encodeURIComponent(ticketId)}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "rlink-token": token,
        },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || `Failed to load ticket (${response.status}).`);
      }
      setDetailTicket(data?.ticket || null);
      setDetailComments(Array.isArray(data?.comments) ? data.comments : []);
    } catch (err) {
      setDetailError(err?.message || "Failed to load ticket.");
    } finally {
      setDetailLoading(false);
    }
  };

  const closeTicketDetail = () => {
    setDetailOpen(false);
    setDetailError("");
    setDetailTicket(null);
    setDetailComments([]);
    setReplyBody("");
    setReplyAttachment(null);
    setReplyError("");
    setReplySuccess("");
    setReplySubmitting(false);
  };

  const submitReply = async () => {
    if (!token || !detailTicket) return;
    const trimmed = replyBody.trim();
    if (trimmed.length < 2) {
      setReplyError("Please enter a reply message.");
      return;
    }
    setReplySubmitting(true);
    setReplyError("");
    setReplySuccess("");
    try {
      const url = `${SUPPORT_TICKETS_URL}/${encodeURIComponent(detailTicket.id)}/comments`;
      let response;
      if (replyAttachment) {
        const form = new FormData();
        form.append("body", trimmed);
        form.append("attachment", replyAttachment);
        response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "rlink-token": token,
          },
          body: form,
        });
      } else {
        response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "rlink-token": token,
          },
          body: JSON.stringify({ body: trimmed }),
        });
      }
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || `Failed to send reply (${response.status}).`);
      }
      if (Array.isArray(data?.comments)) {
        setDetailComments(data.comments);
      }
      if (data?.status && detailTicket) {
        setDetailTicket({ ...detailTicket, status: data.status });
        setTickets((prev) =>
          prev.map((t) =>
            t.id === detailTicket.id
              ? { ...t, status: data.status, updated_at: new Date().toISOString() }
              : t
          )
        );
      }
      setReplyBody("");
      setReplyAttachment(null);
      setReplySuccess("Reply sent! Our team will get back to you shortly.");
    } catch (err) {
      setReplyError(err?.message || "Failed to send reply.");
    } finally {
      setReplySubmitting(false);
    }
  };

  const validate = () => {
    const errors = {};
    if (!category) errors.category = "Please select a category.";
    if (!subject.trim()) errors.subject = "Please enter a subject.";
    else if (subject.length > SUBJECT_MAX) errors.subject = `Subject must be under ${SUBJECT_MAX} characters.`;
    if (!description.trim()) errors.description = "Please describe your issue.";
    else if (description.trim().length < DESCRIPTION_MIN) errors.description = `Description must be at least ${DESCRIPTION_MIN} characters.`;
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    setSuccessMessage("");
    if (!validate()) return;
    if (!token) {
      setSubmitError("You must be signed in to submit a ticket.");
      return;
    }

    setSubmitting(true);
    try {
      const hasAttachment = Boolean(attachment);
      let response;
      if (hasAttachment && attachment) {
        const form = new FormData();
        form.append("category", category);
        form.append("subject", subject.trim());
        form.append("description", description.trim());
        form.append("attachment", attachment);
        response = await fetch(SUPPORT_TICKETS_URL, {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "rlink-token": token,
          },
          body: form,
        });
      } else {
        response = await fetch(SUPPORT_TICKETS_URL, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "rlink-token": token,
          },
          body: JSON.stringify({
            category,
            subject: subject.trim(),
            description: description.trim(),
          }),
        });
      }

      const data = await response.json().catch(() => ({}));

      if (response.status === 429) {
        throw new Error(
          data?.message ||
            "You've reached the submission limit. Please try again later."
        );
      }
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || `Failed to submit ticket (${response.status}).`);
      }

      const newTicket = data?.ticket || null;
      if (newTicket) {
        setTickets((prev) => [newTicket, ...prev.filter((t) => t.id !== newTicket.id)]);
      } else {
        loadTickets();
      }

      setSuccessMessage("Ticket submitted! We'll get back to you by email shortly.");
      setCategory("");
      setSubject("");
      setDescription("");
      setAttachment(null);
      setFieldErrors({});
    } catch (err) {
      setSubmitError(err?.message || "Failed to submit ticket.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputBaseStyle = {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 10,
    border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,18,51,0.1)",
    background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
    color: isDark ? "#ffffff" : "#001233",
    fontSize: 14,
    fontFamily: "'DM Sans', sans-serif",
    outline: "none",
  };

  const errorInputStyle = (hasError) =>
    hasError ? { ...inputBaseStyle, border: "1px solid #ef4444" } : inputBaseStyle;

  const optionStyle = {
    background: isDark ? "#0a1929" : "#ffffff",
    color: isDark ? "#ffffff" : "#001233",
  };

  const labelStyle = {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,18,51,0.5)",
  };

  const cardStyle = {
    background: isDark ? "rgba(255,255,255,0.05)" : "#ffffff",
    borderRadius: 16,
    padding: 24,
    border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,18,51,0.08)",
    boxShadow: isDark ? "none" : "0 2px 12px rgba(0,18,51,0.06)",
    backdropFilter: isDark ? "blur(12px)" : "none",
  };

  return (
    <div className="w-full" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Page Header */}
      <div style={{ marginBottom: 28 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,18,51,0.4)",
            marginBottom: 10,
          }}
        >
          Account
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: "rgba(106,31,191,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LifeBuoy size={18} color="#a64dff" />
          </div>
          <h1
            style={{
              fontSize: 28,
              fontWeight: 800,
              color: isDark ? "#ffffff" : "#001233",
              margin: 0,
            }}
          >
            Help &amp; Support
          </h1>
        </div>
        <p
          style={{
            fontSize: 14,
            color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,18,51,0.55)",
            margin: 0,
          }}
        >
          Get help and access support resources.
        </p>
      </div>

      {/* Zone 1 — Resource cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "1fr"
            : "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 16,
          marginBottom: 24,
        }}
      >
        {RESOURCE_CARDS.map((card) => {
          const Icon = card.icon;
          const isClickable = Boolean(card.href);
          const Wrapper = isClickable ? "a" : "div";
          const wrapperProps = isClickable
            ? { href: card.href, target: "_blank", rel: "noreferrer noopener" }
            : {};
          return (
            <Wrapper
              key={card.key}
              {...wrapperProps}
              style={{
                ...cardStyle,
                padding: 20,
                textDecoration: "none",
                color: "inherit",
                display: "flex",
                flexDirection: "column",
                gap: 14,
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                cursor: isClickable ? "pointer" : "default",
              }}
              onMouseEnter={(e) => {
                if (!isClickable) return;
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 8px 24px rgba(106,31,191,0.18)";
              }}
              onMouseLeave={(e) => {
                if (!isClickable) return;
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = isDark
                  ? "none"
                  : "0 2px 12px rgba(0,18,51,0.06)";
              }}
            >
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  background: `${card.color}22`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon size={20} color={card.color} />
              </div>
              <div>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: isDark ? "#ffffff" : "#001233",
                    marginBottom: 4,
                  }}
                >
                  {card.title}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,18,51,0.55)",
                    lineHeight: 1.45,
                  }}
                >
                  {card.desc}
                </div>
              </div>
            </Wrapper>
          );
        })}
      </div>

      {/* Zone 2 — Contact Support form */}
      <div style={{ ...cardStyle, marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <MessageCircle size={18} color="#a64dff" />
          <h2
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: isDark ? "#ffffff" : "#001233",
              margin: 0,
            }}
          >
            Contact Support
          </h2>
        </div>

        {successMessage && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "12px 14px",
              borderRadius: 10,
              background: "rgba(0,200,83,0.12)",
              border: "1px solid rgba(0,200,83,0.35)",
              color: "#00c853",
              fontSize: 14,
              fontWeight: 500,
              marginBottom: 16,
            }}
            role="status"
          >
            <CheckCircle2 size={18} />
            {successMessage}
          </div>
        )}

        {submitError && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "12px 14px",
              borderRadius: 10,
              background: "rgba(239,68,68,0.12)",
              border: "1px solid rgba(239,68,68,0.35)",
              color: "#ef4444",
              fontSize: 14,
              fontWeight: 500,
              marginBottom: 16,
            }}
            role="alert"
          >
            <AlertCircle size={18} />
            {submitError}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 16,
              marginBottom: 16,
            }}
          >
            {/* Category */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={labelStyle} htmlFor="support-category">
                Category <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <select
                id="support-category"
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value);
                  if (fieldErrors.category) setFieldErrors((f) => ({ ...f, category: undefined }));
                }}
                style={{ ...errorInputStyle(Boolean(fieldErrors.category)), cursor: "pointer" }}
              >
                <option value="" disabled style={optionStyle}>Select category</option>
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value} style={optionStyle}>{c.label}</option>
                ))}
              </select>
              {fieldErrors.category && (
                <div style={{ fontSize: 12, color: "#ef4444" }}>{fieldErrors.category}</div>
              )}
            </div>

            {/* Subject */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={labelStyle} htmlFor="support-subject">
                Subject <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                id="support-subject"
                type="text"
                placeholder="Brief description of your issue"
                maxLength={SUBJECT_MAX}
                value={subject}
                onChange={(e) => {
                  setSubject(e.target.value);
                  if (fieldErrors.subject) setFieldErrors((f) => ({ ...f, subject: undefined }));
                }}
                style={errorInputStyle(Boolean(fieldErrors.subject))}
              />
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <div style={{ fontSize: 12, color: "#ef4444" }}>{fieldErrors.subject || ""}</div>
                <div
                  style={{
                    fontSize: 11,
                    color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,18,51,0.4)",
                  }}
                >
                  {subject.length}/{SUBJECT_MAX}
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
            <label style={labelStyle} htmlFor="support-description">
              Description <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <textarea
              id="support-description"
              rows={6}
              placeholder="Please provide details about your issue or question..."
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                if (fieldErrors.description) setFieldErrors((f) => ({ ...f, description: undefined }));
              }}
              style={{
                ...errorInputStyle(Boolean(fieldErrors.description)),
                resize: "vertical",
                minHeight: 120,
              }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <div style={{ fontSize: 12, color: "#ef4444" }}>{fieldErrors.description || ""}</div>
              <div
                style={{
                  fontSize: 11,
                  color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,18,51,0.4)",
                }}
              >
                {description.trim().length} chars · min {DESCRIPTION_MIN}
              </div>
            </div>
          </div>

          {/* Attachment */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20 }}>
            <label style={labelStyle}>Attachment (optional)</label>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <label
                htmlFor="support-attachment"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "9px 14px",
                  borderRadius: 10,
                  border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,18,51,0.15)",
                  background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                  color: isDark ? "rgba(255,255,255,0.8)" : "#001233",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                <Paperclip size={14} />
                Choose file
                <input
                  id="support-attachment"
                  type="file"
                  onChange={(e) => setAttachment(e.target.files?.[0] || null)}
                  style={{ display: "none" }}
                />
              </label>
              {attachment && (
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 10px",
                    borderRadius: 8,
                    background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,18,51,0.05)",
                    fontSize: 12,
                    color: isDark ? "rgba(255,255,255,0.8)" : "#001233",
                  }}
                >
                  <span>{attachment.name}</span>
                  <button
                    type="button"
                    onClick={() => setAttachment(null)}
                    style={{
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,18,51,0.6)",
                      padding: 0,
                      display: "inline-flex",
                    }}
                    aria-label="Remove attachment"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
              <div
                style={{
                  fontSize: 12,
                  color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,18,51,0.4)",
                }}
              >
                Screenshots or screen recordings help us help you faster.
              </div>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              type="submit"
              disabled={submitting}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 20px",
                borderRadius: 10,
                border: "none",
                background: submitting
                  ? "rgba(106,31,191,0.5)"
                  : "linear-gradient(135deg, #6a1fbf, #00c853)",
                color: "#ffffff",
                fontSize: 14,
                fontWeight: 700,
                cursor: submitting ? "not-allowed" : "pointer",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                opacity: submitting ? 0.7 : 1,
              }}
              onMouseEnter={(e) => {
                if (submitting) return;
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 8px 16px rgba(106,31,191,0.3)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <Send size={16} />
              {submitting ? "Submitting..." : "Submit Ticket"}
            </button>
          </div>
        </form>
      </div>

      {/* Zone 3 — Recent Tickets */}
      <div style={cardStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <Activity size={18} color="#a64dff" />
          <h2
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: isDark ? "#ffffff" : "#001233",
              margin: 0,
            }}
          >
            Recent Tickets
          </h2>
        </div>

        {ticketsError && (
          <div
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              background: "rgba(239,68,68,0.1)",
              border: "1px solid rgba(239,68,68,0.25)",
              color: "#ef4444",
              fontSize: 13,
              marginBottom: 14,
            }}
          >
            {ticketsError}
          </div>
        )}

        {ticketsLoading ? (
          <div
            style={{
              fontSize: 14,
              color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,18,51,0.55)",
              padding: "12px 0",
            }}
          >
            Loading tickets...
          </div>
        ) : tickets.length === 0 ? (
          <div
            style={{
              padding: "32px 16px",
              textAlign: "center",
              color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,18,51,0.55)",
              fontSize: 14,
            }}
          >
            No support tickets yet. Use the form above to get in touch with our team.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {tickets.map((ticket) => {
              const status = STATUS_STYLES[ticket.status] || STATUS_STYLES.open;
              return (
                <div
                  key={ticket.id}
                  onClick={() => openTicketDetail(ticket.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openTicketDetail(ticket.id);
                    }
                  }}
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "120px 1fr auto auto",
                    alignItems: "center",
                    gap: 14,
                    padding: "14px 16px",
                    borderRadius: 12,
                    background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,18,51,0.02)",
                    border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,18,51,0.06)",
                    cursor: "pointer",
                    transition: "background 0.15s ease, border-color 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = isDark
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(106,31,191,0.04)";
                    e.currentTarget.style.borderColor = isDark
                      ? "rgba(255,255,255,0.12)"
                      : "rgba(106,31,191,0.18)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = isDark
                      ? "rgba(255,255,255,0.03)"
                      : "rgba(0,18,51,0.02)";
                    e.currentTarget.style.borderColor = isDark
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,18,51,0.06)";
                  }}
                >
                  <div
                    style={{
                      fontFamily: "'JetBrains Mono', 'Menlo', monospace",
                      fontSize: 12,
                      fontWeight: 600,
                      color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,18,51,0.7)",
                    }}
                  >
                    {formatTicketNumber(ticket)}
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: isDark ? "#ffffff" : "#001233",
                    }}
                  >
                    {ticket.subject}
                  </div>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "4px 10px",
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.03em",
                      background: status.bg,
                      color: status.color,
                      justifySelf: isMobile ? "start" : "end",
                    }}
                  >
                    {status.label}
                  </span>
                  <div
                    style={{
                      fontSize: 12,
                      color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,18,51,0.5)",
                      textAlign: isMobile ? "left" : "right",
                    }}
                  >
                    {formatRelativeTime(ticket.updated_at || ticket.created_at)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {detailOpen && (
        <div
          onClick={closeTicketDetail}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            zIndex: 1000,
            backdropFilter: "blur(2px)",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: isDark ? "#0d1b3e" : "#ffffff",
              color: isDark ? "#ffffff" : "#001233",
              borderRadius: 16,
              border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,18,51,0.08)",
              maxWidth: 720,
              width: "100%",
              maxHeight: "85vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 14,
                padding: "18px 20px",
                borderBottom: isDark
                  ? "1px solid rgba(255,255,255,0.08)"
                  : "1px solid rgba(0,18,51,0.08)",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontFamily: "'JetBrains Mono', 'Menlo', monospace",
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,18,51,0.5)",
                    marginBottom: 4,
                  }}
                >
                  {detailTicket ? formatTicketNumber(detailTicket) : "Ticket"}
                </div>
                <h3
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    margin: 0,
                    wordBreak: "break-word",
                  }}
                >
                  {detailTicket?.subject || (detailLoading ? "Loading…" : "Ticket details")}
                </h3>
                {detailTicket && (
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "center",
                      marginTop: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "3px 10px",
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.03em",
                        background: (STATUS_STYLES[detailTicket.status] || STATUS_STYLES.open).bg,
                        color: (STATUS_STYLES[detailTicket.status] || STATUS_STYLES.open).color,
                      }}
                    >
                      {(STATUS_STYLES[detailTicket.status] || STATUS_STYLES.open).label}
                    </span>
                    {detailTicket.category && (
                      <span
                        style={{
                          fontSize: 12,
                          color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,18,51,0.6)",
                        }}
                      >
                        {detailTicket.category}
                      </span>
                    )}
                    <span
                      style={{
                        fontSize: 12,
                        color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,18,51,0.5)",
                      }}
                    >
                      Opened {formatRelativeTime(detailTicket.created_at)}
                    </span>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={closeTicketDetail}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,18,51,0.6)",
                  padding: 4,
                  display: "inline-flex",
                  borderRadius: 8,
                }}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ overflowY: "auto", padding: "16px 20px", flex: 1, minHeight: 0 }}>
              {detailError && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "12px 14px",
                    borderRadius: 10,
                    background: "rgba(239,68,68,0.12)",
                    border: "1px solid rgba(239,68,68,0.35)",
                    color: "#ef4444",
                    fontSize: 14,
                    marginBottom: 12,
                  }}
                  role="alert"
                >
                  <AlertCircle size={18} />
                  {detailError}
                </div>
              )}

              {detailLoading ? (
                <div
                  style={{
                    padding: "40px 0",
                    textAlign: "center",
                    fontSize: 14,
                    color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,18,51,0.55)",
                  }}
                >
                  Loading conversation…
                </div>
              ) : detailComments.length === 0 && !detailError ? (
                <div
                  style={{
                    padding: "40px 0",
                    textAlign: "center",
                    fontSize: 14,
                    color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,18,51,0.55)",
                  }}
                >
                  No messages on this ticket yet.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {[...detailComments].reverse().map((comment) => {
                    const isAgent = comment.author_role === "agent";
                    return (
                      <div
                        key={comment.id}
                        style={{
                          padding: "14px 16px",
                          borderRadius: 12,
                          background: isAgent
                            ? isDark
                              ? "rgba(106,31,191,0.15)"
                              : "rgba(106,31,191,0.08)"
                            : isDark
                            ? "rgba(255,255,255,0.04)"
                            : "rgba(0,18,51,0.03)",
                          border: isAgent
                            ? "1px solid rgba(106,31,191,0.3)"
                            : isDark
                            ? "1px solid rgba(255,255,255,0.06)"
                            : "1px solid rgba(0,18,51,0.06)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "baseline",
                            gap: 10,
                            marginBottom: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: isDark ? "#ffffff" : "#001233",
                              }}
                            >
                              {comment.author_name || comment.author_email || (isAgent ? "Support" : "You")}
                            </span>
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: "0.04em",
                                textTransform: "uppercase",
                                padding: "2px 8px",
                                borderRadius: 999,
                                background: isAgent ? "rgba(106,31,191,0.25)" : "rgba(0,200,83,0.2)",
                                color: isAgent ? "#a64dff" : "#00c853",
                              }}
                            >
                              {isAgent ? "Support" : "You"}
                            </span>
                            {!comment.public && (
                              <span
                                style={{
                                  fontSize: 10,
                                  fontWeight: 600,
                                  padding: "2px 8px",
                                  borderRadius: 999,
                                  background: "rgba(245,158,11,0.2)",
                                  color: "#f59e0b",
                                }}
                              >
                                Internal
                              </span>
                            )}
                          </div>
                          <span
                            style={{
                              fontSize: 11,
                              color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,18,51,0.5)",
                            }}
                          >
                            {formatRelativeTime(comment.created_at)}
                          </span>
                        </div>
                        <div
                          style={{
                            fontSize: 14,
                            lineHeight: 1.55,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            color: isDark ? "rgba(255,255,255,0.9)" : "#001233",
                          }}
                        >
                          {comment.body}
                        </div>
                        {Array.isArray(comment.attachments) && comment.attachments.length > 0 && (
                          <div
                            style={{
                              marginTop: 10,
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 8,
                            }}
                          >
                            {comment.attachments.map((a) => (
                              <a
                                key={a.id}
                                href={a.content_url}
                                target="_blank"
                                rel="noreferrer noopener"
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 6,
                                  padding: "6px 10px",
                                  borderRadius: 8,
                                  background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,18,51,0.06)",
                                  color: isDark ? "#ffffff" : "#001233",
                                  fontSize: 12,
                                  textDecoration: "none",
                                }}
                              >
                                <Paperclip size={12} />
                                {a.file_name}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {detailTicket && detailTicket.status !== "closed" && (
              <div
                style={{
                  borderTop: "3px solid #3b82f6",
                  padding: "16px 20px 18px",
                  background: isDark
                    ? "linear-gradient(180deg, rgba(106,31,191,0.14), rgba(0,200,83,0.06))"
                    : "linear-gradient(180deg, rgba(106,31,191,0.08), rgba(0,200,83,0.04))",
                  boxShadow: isDark
                    ? "0 -10px 24px rgba(59,130,246,0.22), inset 0 1px 0 rgba(59,130,246,0.35)"
                    : "0 -10px 24px rgba(59,130,246,0.18), inset 0 1px 0 rgba(59,130,246,0.35)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 10,
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      color: "#a64dff",
                    }}
                  >
                    <MessageCircle size={14} />
                    Add a reply
                  </div>
                </div>

                {replySuccess && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 12px",
                      borderRadius: 8,
                      background: "rgba(0,200,83,0.14)",
                      border: "1px solid rgba(0,200,83,0.4)",
                      color: "#00c853",
                      fontSize: 13,
                      fontWeight: 500,
                      marginBottom: 10,
                    }}
                    role="status"
                  >
                    <CheckCircle2 size={14} />
                    {replySuccess}
                  </div>
                )}

                {replyError && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 12px",
                      borderRadius: 8,
                      background: "rgba(239,68,68,0.12)",
                      border: "1px solid rgba(239,68,68,0.35)",
                      color: "#ef4444",
                      fontSize: 13,
                      marginBottom: 10,
                    }}
                    role="alert"
                  >
                    <AlertCircle size={14} />
                    {replyError}
                  </div>
                )}

                <textarea
                  rows={3}
                  placeholder="Type your reply..."
                  value={replyBody}
                  onChange={(e) => {
                    setReplyBody(e.target.value);
                    if (replyError) setReplyError("");
                  }}
                  disabled={replySubmitting}
                  style={{
                    ...inputBaseStyle,
                    resize: "vertical",
                    minHeight: 80,
                    marginBottom: 10,
                  }}
                />

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <label
                      htmlFor="reply-attachment"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "7px 12px",
                        borderRadius: 8,
                        border: isDark
                          ? "1px solid rgba(255,255,255,0.15)"
                          : "1px solid rgba(0,18,51,0.15)",
                        background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                        color: isDark ? "rgba(255,255,255,0.8)" : "#001233",
                        fontSize: 12,
                        fontWeight: 500,
                        cursor: replySubmitting ? "not-allowed" : "pointer",
                        opacity: replySubmitting ? 0.6 : 1,
                      }}
                    >
                      <Paperclip size={13} />
                      Attach
                      <input
                        id="reply-attachment"
                        type="file"
                        disabled={replySubmitting}
                        onChange={(e) => setReplyAttachment(e.target.files?.[0] || null)}
                        style={{ display: "none" }}
                      />
                    </label>
                    {replyAttachment && (
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "5px 9px",
                          borderRadius: 8,
                          background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,18,51,0.05)",
                          fontSize: 12,
                          color: isDark ? "rgba(255,255,255,0.8)" : "#001233",
                          maxWidth: 220,
                        }}
                      >
                        <span
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {replyAttachment.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => setReplyAttachment(null)}
                          disabled={replySubmitting}
                          style={{
                            background: "transparent",
                            border: "none",
                            cursor: replySubmitting ? "not-allowed" : "pointer",
                            color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,18,51,0.6)",
                            padding: 0,
                            display: "inline-flex",
                          }}
                          aria-label="Remove attachment"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={submitReply}
                    disabled={replySubmitting || !replyBody.trim()}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "9px 18px",
                      borderRadius: 10,
                      border: "none",
                      background:
                        replySubmitting || !replyBody.trim()
                          ? "rgba(106,31,191,0.5)"
                          : "linear-gradient(135deg, #6a1fbf, #00c853)",
                      color: "#ffffff",
                      fontSize: 13,
                      fontWeight: 700,
                      cursor:
                        replySubmitting || !replyBody.trim() ? "not-allowed" : "pointer",
                      opacity: replySubmitting || !replyBody.trim() ? 0.7 : 1,
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    }}
                  >
                    <Send size={14} />
                    {replySubmitting ? "Sending..." : "Send Reply"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpSupport;
